import { Typography } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";

interface LocationDetails {
  address: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  country?: string;
}

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  onLocationSelect: (details: LocationDetails) => void;
}

// Declare Leaflet for TypeScript
declare const L: any;

export const LocationModal: React.FC<LocationModalProps> = ({
  open,
  onClose,
  onLocationSelect,
}) => {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const [mapType, setMapType] = useState<"google" | "leaflet" | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<any>(null);
  const googleMarkerRef = useRef<any>(null);
  const autocompleteRef = useRef<any>(null);
  const leafletMapRef = useRef<any>(null);
  const leafletMarkerRef = useRef<any>(null);

  // Track if we've attempted to load scripts
  const loadAttemptedRef = useRef(false);

  // Load map libraries when modal opens
  useEffect(() => {
    if (!open || loadAttemptedRef.current) return;

    loadAttemptedRef.current = true;
    loadMapLibraries();
  }, [open]);

  const loadMapLibraries = async () => {
    // Try Google Maps first
    const googleApiKey: string = "AIzaSyA_MfAni9Zm0XM78gvK5EHdT8uYxtuMRt8"; // Replace with valid key

    // Check if key looks valid (basic check)
    if (
      googleApiKey &&
      googleApiKey !== "YOUR_API_KEY_HERE" &&
      googleApiKey.length > 20
    ) {
      try {
        await loadGoogleMaps(googleApiKey);
        setMapType("google");
        setMapReady(true);
        return;
      } catch (err) {
        console.warn("Google Maps failed, falling back to Leaflet:", err);
      }
    }

    // Fallback to Leaflet
    try {
      await loadLeaflet();
      setMapType("leaflet");
      setMapReady(true);
    } catch (err) {
      console.error("Failed to load any map library:", err);
      setError("Failed to load map. Please try again.");
    }
  };

  const loadGoogleMaps = (apiKey: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google?.maps?.places) {
        resolve();
        return;
      }

      const callbackName = `initMap_${Date.now()}`;

      (window as any)[callbackName] = () => {
        delete (window as any)[callbackName];
        resolve();
      };

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        delete (window as any)[callbackName];
        reject(new Error("Failed to load Google Maps"));
      };

      document.head.appendChild(script);
    });
  };

  const loadLeaflet = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (typeof L !== "undefined") {
        resolve();
        return;
      }

      // Load CSS
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const cssLink = document.createElement("link");
        cssLink.rel = "stylesheet";
        cssLink.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(cssLink);
      }

      // Load JS
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Leaflet"));
      document.head.appendChild(script);
    });
  };

  // Initialize map after libraries are loaded and modal is open
  useEffect(() => {
    if (!open || !mapReady || !mapContainerRef.current) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (mapType === "google") {
        initGoogleMap();
      } else if (mapType === "leaflet") {
        initLeafletMap();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [open, mapReady, mapType]);

  const initGoogleMap = () => {
    if (!mapContainerRef.current || googleMapRef.current) return;

    try {
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: 9.082, lng: 8.6753 }, // Nigeria center
        zoom: 6,
        mapTypeControl: true,
        streetViewControl: false,
      });

      map.addListener("click", (e: any) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        handleMapClick(lat, lng);
      });

      googleMapRef.current = map;

      // Initialize autocomplete
      if (searchInputRef.current) {
        const autocomplete = new window.google.maps.places.Autocomplete(
          searchInputRef.current,
          {
            componentRestrictions: { country: "ng" },
            fields: ["address_components", "geometry", "formatted_address"],
          }
        );

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            handlePlaceSelect(place, lat, lng);
          }
        });

        autocompleteRef.current = autocomplete;
      }
    } catch (err) {
      console.error("Error initializing Google Map:", err);
      setError("Failed to initialize map");
    }
  };

  const initLeafletMap = () => {
    if (!mapContainerRef.current || leafletMapRef.current) return;

    try {
      // Clear any existing content
      mapContainerRef.current.innerHTML = "";

      const map = L.map(mapContainerRef.current).setView([9.082, 8.6753], 6);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      map.on("click", (e: any) => {
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        handleMapClick(lat, lng);
      });

      leafletMapRef.current = map;

      // Fix sizing issues
      setTimeout(() => {
        map.invalidateSize();
      }, 200);
    } catch (err) {
      console.error("Error initializing Leaflet Map:", err);
      setError("Failed to initialize map");
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setLatitude(lat.toFixed(6));
    setLongitude(lng.toFixed(6));
    placeMarker(lat, lng);
    reverseGeocode(lat, lng);
  };

  const handlePlaceSelect = (place: any, lat: number, lng: number) => {
    setLatitude(lat.toFixed(6));
    setLongitude(lng.toFixed(6));
    setAddress(place.formatted_address || "");

    if (place.address_components) {
      extractAddressComponents(place.address_components);
    }

    placeMarker(lat, lng);
  };

  const extractAddressComponents = (components: any[]) => {
    let cityName = "";
    let stateName = "";

    components.forEach((component: any) => {
      if (component.types.includes("locality")) {
        cityName = component.long_name;
      } else if (
        component.types.includes("administrative_area_level_2") &&
        !cityName
      ) {
        cityName = component.long_name;
      }
      if (component.types.includes("administrative_area_level_1")) {
        stateName = component.long_name;
      }
    });

    setCity(cityName);
    setState(stateName);
  };

  const placeMarker = (lat: number, lng: number) => {
    if (mapType === "google" && googleMapRef.current) {
      placeGoogleMarker(lat, lng);
    } else if (mapType === "leaflet" && leafletMapRef.current) {
      placeLeafletMarker(lat, lng);
    }
  };

  const placeGoogleMarker = (lat: number, lng: number) => {
    const position = { lat, lng };
    googleMapRef.current.setCenter(position);
    googleMapRef.current.setZoom(15);

    if (googleMarkerRef.current) {
      googleMarkerRef.current.setMap(null);
    }

    googleMarkerRef.current = new window.google.maps.Marker({
      position,
      map: googleMapRef.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    googleMarkerRef.current.addListener("dragend", (e: any) => {
      const newLat = e.latLng.lat();
      const newLng = e.latLng.lng();
      handleMapClick(newLat, newLng);
    });
  };

  const placeLeafletMarker = (lat: number, lng: number) => {
    leafletMapRef.current.setView([lat, lng], 15);

    if (leafletMarkerRef.current) {
      leafletMapRef.current.removeLayer(leafletMarkerRef.current);
    }

    leafletMarkerRef.current = L.marker([lat, lng], {
      draggable: true,
    }).addTo(leafletMapRef.current);

    leafletMarkerRef.current.on("dragend", (e: any) => {
      const pos = e.target.getLatLng();
      handleMapClick(pos.lat, pos.lng);
    });
  };

  const reverseGeocode = async (lat: number, lng: number) => {
    if (mapType === "google" && window.google?.maps) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat, lng } },
          (results: any, status: any) => {
            if (status === "OK" && results?.[0]) {
              setAddress(results[0].formatted_address);
              extractAddressComponents(results[0].address_components);
            }
          }
        );
      } catch (err) {
        console.error("Geocoding error:", err);
      }
    } else {
      // Use Nominatim for Leaflet
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
          { headers: { "User-Agent": "LocationModal/1.0" } }
        );
        const data = await response.json();
        if (data?.display_name) {
          setAddress(data.display_name);
          if (data.address) {
            setCity(data.address.city || data.address.town || "");
            setState(data.address.state || "");
          }
        }
      } catch (err) {
        console.error("Geocoding error:", err);
      }
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        handleMapClick(lat, lng);
        setLoading(false);
      },
      (err) => {
        setError("Unable to get location. Check permissions.");
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleConfirm = () => {
    if (!latitude || !longitude || !address) {
      setError("Please provide all required fields");
      return;
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (
      isNaN(lat) ||
      isNaN(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      setError("Invalid coordinates");
      return;
    }

    onLocationSelect({
      address: address.trim(),
      latitude: lat,
      longitude: lng,
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      country: "Nigeria",
    });

    handleClose();
  };

  const handleClose = () => {
    // Cleanup
    if (googleMarkerRef.current) {
      googleMarkerRef.current.setMap(null);
    }
    if (leafletMarkerRef.current && leafletMapRef.current) {
      leafletMapRef.current.removeLayer(leafletMarkerRef.current);
    }
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
    }

    googleMapRef.current = null;
    googleMarkerRef.current = null;
    autocompleteRef.current = null;
    leafletMarkerRef.current = null;

    setLatitude("");
    setLongitude("");
    setAddress("");
    setCity("");
    setState("");
    setError("");
    setMapReady(false);

    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          maxWidth: "800px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "32px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <Typography style={{ margin: 0, fontSize: "24px" }}>
            Select Facility Location
          </Typography>
          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              padding: "0",
              width: "32px",
              height: "32px",
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "8px",
              marginBottom: "16px",
              color: "#c00",
            }}
          >
            {error}
          </div>
        )}

        {/* Search (Google only) */}
        {mapType === "google" && (
          <div style={{ marginBottom: "16px" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for a location in Nigeria..."
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>
        )}

        {/* Current Location Button */}
        <button
          onClick={getCurrentLocation}
          disabled={loading || !mapReady}
          style={{
            width: "100%",
            padding: "12px",
            marginBottom: "16px",
            border: "2px solid #1976d2",
            backgroundColor: "white",
            color: "#1976d2",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: loading || !mapReady ? "not-allowed" : "pointer",
            opacity: loading || !mapReady ? 0.6 : 1,
          }}
        >
          {loading ? "Getting Location..." : "📍 Use My Current Location"}
        </button>

        {/* Map */}
        <div
          style={{
            width: "100%",
            height: "400px",
            border: "2px solid #ddd",
            borderRadius: "8px",
            marginBottom: "16px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            ref={mapContainerRef}
            style={{ width: "100%", height: "100%" }}
          />
          {!mapReady && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.9)",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #1976d2",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 12px",
                  }}
                />
                <p>Loading map...</p>
              </div>
            </div>
          )}
        </div>

        {/* Coordinates */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px", // space between items
            marginBottom: "16px",
          }}
        >
          <div style={{ flex: "1 1 300px", minWidth: "200px" }}>
            <Typography
              style={{
                display: "block",
                marginBottom: "4px",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Latitude *
            </Typography>
            <input
              type="number"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              placeholder="e.g., 4.8156"
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
              }}
            />
          </div>
          <div style={{ flex: "1 1 300px", minWidth: "200px" }}>
            <Typography
              style={{
                display: "block",
                marginBottom: "4px",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Longitude *
            </Typography>
            <input
              type="number"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="e.g., 7.0498"
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>

        {/* Address */}
        <div style={{ marginBottom: "16px" }}>
          <Typography
            style={{
              display: "block",
              marginBottom: "4px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Address *
          </Typography>
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Enter full address"
            rows={2}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
        </div>

        {/* City & State */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px", // space between items
            marginBottom: "16px",
          }}
        >
          <div style={{ flex: "1 1 300px", minWidth: "200px" }}>
            <Typography
              style={{
                display: "block",
                marginBottom: "4px",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              City
            </Typography>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
              }}
            />
          </div>
          <div style={{ flex: "1 1 300px", minWidth: "200px" }}>
            <Typography
              style={{
                display: "block",
                marginBottom: "4px",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              State
            </Typography>

            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>

        {/* Buttons */}
        <div
          style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: "12px 24px",
              border: "1px solid #ddd",
              backgroundColor: "white",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!mapReady}
            style={{
              padding: "12px 32px",
              border: "none",
              backgroundColor: "#1976d2",
              color: "white",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: mapReady ? "pointer" : "not-allowed",
              opacity: mapReady ? 1 : 0.6,
            }}
          >
            Confirm Location
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
